/**
 * Office.js Service for Word Add-in
 * 
 * This service provides a clean API for interacting with Word documents.
 * It handles both Office.js environment and fallback for web-only testing.
 */

// Extend Window interface for Office.js globals
declare global {
  interface Window {
    officeReady?: boolean;
    officeHost?: string;
    officePlatform?: string;
    Office?: any;
    Word?: any;
  }
}

// Office.js type stubs
declare const Word: any;
declare const Office: any;

type OfficeListener = (isWord: boolean) => void;

class OfficeService {
  private isOfficeReady: boolean = false;
  private isWord: boolean = false;
  private listeners: OfficeListener[] = [];

  constructor() {
    // Check if already ready (set by index.js)
    if (typeof window !== 'undefined') {
      if (window.officeReady) {
        this.isOfficeReady = true;
        // Word host can be 'Word' or 'Document'
        this.isWord = window.officeHost === 'Word' || window.officeHost === 'Document';
        console.log('OfficeService: Already initialized', {
          host: window.officeHost,
          platform: window.officePlatform,
          isWord: this.isWord
        });
      }
    }
  }
  
  /**
   * Initialize with Office info (called from index.js)
   */
  initialize(host: string, platform: string): void {
    this.isOfficeReady = true;
    this.isWord = host === 'Word' || host === 'Document';
    console.log('OfficeService: Initialized', { host, platform, isWord: this.isWord });
    this.notifyListeners();
  }
  
  /**
   * Check if running in Office environment
   */
  isInOffice(): boolean {
    // Also check window.officeReady as backup
    if (typeof window !== 'undefined' && window.officeReady) {
      return true;
    }
    return this.isOfficeReady && this.isWord;
  }
  
  /**
   * Add listener for Office ready state
   */
  onReady(callback: OfficeListener): void {
    if (this.isOfficeReady || (typeof window !== 'undefined' && window.officeReady)) {
      const isWord = this.isWord || window.officeHost === 'Word' || window.officeHost === 'Document';
      callback(isWord);
    } else {
      this.listeners.push(callback);
    }
  }
  
  notifyListeners(): void {
    this.listeners.forEach(cb => cb(this.isWord));
    this.listeners = [];
  }
  
  /**
   * Get currently selected text from Word document
   */
  async getSelection(): Promise<{ text: string; range: any }> {
    if (!this.isInOffice()) {
      console.log('OfficeService: Not in Office, returning empty selection');
      return { text: '', range: null };
    }
    
    try {
      return await Word.run(async (context: any) => {
        const selection = context.document.getSelection();
        selection.load('text');
        await context.sync();
        
        return {
          text: selection.text?.trim() || '',
          range: selection // Keep reference for later use
        };
      });
    } catch (error) {
      console.error('OfficeService: Error getting selection:', error);
      return { text: '', range: null };
    }
  }
  
  /**
   * Insert text at the current selection
   */
  async insertText(text: string, mode: string = 'replace'): Promise<boolean> {
    if (!this.isInOffice()) {
      console.log('OfficeService: Not in Office, cannot insert text');
      return false;
    }
    
    const insertLocation = ({
      'replace': Word.InsertLocation.replace,
      'end': Word.InsertLocation.end,
      'start': Word.InsertLocation.start
    } as Record<string, any>)[mode] || Word.InsertLocation.replace;
    
    try {
      await Word.run(async (context: any) => {
        const selection = context.document.getSelection();
        selection.insertText(text, insertLocation);
        await context.sync();
      });
      return true;
    } catch (error) {
      console.error('OfficeService: Error inserting text:', error);
      return false;
    }
  }
  
  /**
   * Insert text with tracked changes simulation
   * Note: Office.js doesn't support native track changes, so we use visual formatting
   * @param {string} newText - New text to insert
   * @param {string} originalText - Original text being replaced
   */
  async insertTrackedChange(newText, originalText) {
    if (!this.isInOffice()) {
      console.log('OfficeService: Not in Office, cannot insert tracked change');
      return false;
    }
    
    try {
      await Word.run(async (context) => {
        const selection = context.document.getSelection();
        
        // Since Office.js doesn't support native track changes,
        // we insert the new text and add a comment with the change info
        selection.insertText(newText, Word.InsertLocation.replace);
        
        // Get the new range after insertion
        const newRange = context.document.getSelection();
        newRange.load('text');
        await context.sync();
        
        // Add a comment explaining the change
        if (originalText) {
          try {
            newRange.insertComment(`Lexy AI endring: Erstattet "${originalText.substring(0, 50)}${originalText.length > 50 ? '...' : ''}" med ny tekst.`);
          } catch (commentError) {
            console.log('OfficeService: Could not add comment:', commentError);
          }
        }
        
        await context.sync();
      });
      return true;
    } catch (error) {
      console.error('OfficeService: Error inserting tracked change:', error);
      return false;
    }
  }
  
  /**
   * Add a comment to the current selection
   * @param {string} commentText - The comment text
   */
  async addComment(commentText) {
    if (!this.isInOffice()) {
      console.log('OfficeService: Not in Office, cannot add comment');
      return false;
    }
    
    try {
      await Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.insertComment(commentText);
        await context.sync();
      });
      return true;
    } catch (error) {
      console.error('OfficeService: Error adding comment:', error);
      return false;
    }
  }
  
  /**
   * Get the entire document text
   * @returns {Promise<string>}
   */
  async getDocumentText() {
    if (!this.isInOffice()) {
      console.log('OfficeService: Not in Office, returning empty document');
      return '';
    }
    
    try {
      return await Word.run(async (context) => {
        const body = context.document.body;
        body.load('text');
        await context.sync();
        return body.text || '';
      });
    } catch (error) {
      console.error('OfficeService: Error getting document text:', error);
      return '';
    }
  }
  
  /**
   * Get document properties (name, etc.)
   * @returns {Promise<object>}
   */
  async getDocumentProperties() {
    if (!this.isInOffice()) {
      return { title: '', author: '', filename: '' };
    }
    
    try {
      return await Word.run(async (context) => {
        const properties = context.document.properties;
        properties.load(['title', 'author', 'lastAuthor', 'creationDate']);
        await context.sync();
        
        // Try to get filename from Office context
        let filename = '';
        try {
          if (typeof Office !== 'undefined' && Office.context && Office.context.document && Office.context.document.url) {
            const url = Office.context.document.url;
            if (url && typeof url === 'string') {
              // Extract filename from URL or path
              const parts = url.split(/[/\\]/);
              const lastPart = parts[parts.length - 1];
              if (lastPart && typeof lastPart === 'string') {
                // Remove file:/// prefix if present
                filename = lastPart.replace(/^file:\/\/\//, '');
              }
            }
          }
        } catch (e) {
          console.log('Could not get filename from Office.context:', e);
        }
        
        // Use title, filename, or empty string
        const title = (properties.title && properties.title !== 'Untitled') 
          ? properties.title 
          : (filename || '');
        
        return {
          title: title,
          filename: filename,
          author: properties.author || '',
          lastAuthor: properties.lastAuthor || '',
          creationDate: properties.creationDate
        };
      });
    } catch (error) {
      console.error('OfficeService: Error getting document properties:', error);
      return { title: '', author: '', filename: '' };
    }
  }

  /**
   * Read Lexy metadata from Custom XML part in the document
   * This metadata is embedded when opening a document from Lexy
   * XML format: <lexy:lexyProperties xmlns:lexy="http://lexy.no/word-addin/properties">
   *               <lexy:lexy_document_id>...</lexy:lexy_document_id>
   *               ...
   *             </lexy:lexyProperties>
   * @returns {Promise<object|null>} Lexy metadata or null if not found
   */
  async getLexyMetadata() {
    if (!this.isInOffice()) {
      console.log('OfficeService: Not in Office, cannot read Lexy metadata');
      return null;
    }

    try {
      return await Word.run(async (context) => {
        // Get all custom XML parts
        const customXmlParts = context.document.customXmlParts;
        customXmlParts.load('items');
        await context.sync();

        console.log(`OfficeService: Found ${customXmlParts.items.length} custom XML parts`);

        // Try each custom XML part
        for (let i = 0; i < customXmlParts.items.length; i++) {
          const part = customXmlParts.items[i];
          
          try {
            // Get the XML content
            const xmlResult = part.getXml();
            await context.sync();
            
            const xmlContent = xmlResult.value;
            console.log(`OfficeService: Custom XML part ${i}:`, xmlContent.substring(0, 300));
            
            // Check if this looks like Lexy metadata (check for our namespace or element names)
            if (xmlContent.includes('lexy.no/word-addin') || 
                xmlContent.includes('lexy_document_id') || 
                xmlContent.includes('lexyProperties')) {
              
              // Parse the XML to extract metadata
              const parser = new DOMParser();
              const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
              
              // Lexy namespace
              const LEXY_NS = 'http://lexy.no/word-addin/properties';
              
              // Helper to get element text by tag name (handles namespaces)
              const getElementText = (tagName) => {
                // Try with full Lexy namespace (lexy:lexy_document_id)
                let elements = xmlDoc.getElementsByTagNameNS(LEXY_NS, tagName);
                if (elements.length > 0) {
                  return elements[0].textContent?.trim() || null;
                }
                
                // Try prefixed version (lexy:lexy_document_id)
                let element = xmlDoc.getElementsByTagName(`lexy:${tagName}`)[0];
                if (element) {
                  return element.textContent?.trim() || null;
                }
                
                // Try without namespace prefix
                element = xmlDoc.getElementsByTagName(tagName)[0];
                if (element) {
                  return element.textContent?.trim() || null;
                }
                
                return null;
              };
              
              // Try to get document ID - Lexy uses lexy_document_id
              const documentId = getElementText('lexy_document_id') || getElementText('document_id');
              const caseId = getElementText('lexy_case_id') || getElementText('case_id');
              
              // Only return if we found meaningful data
              if (documentId || caseId) {
                const metadata = {
                  documentId: documentId,
                  caseId: caseId,
                  version: parseInt(getElementText('lexy_version') || getElementText('version') || '1', 10),
                  tenantId: getElementText('lexy_tenant_id') || getElementText('tenant_id'),
                  openedAt: getElementText('lexy_opened_at') || getElementText('opened_at'),
                  openedBy: getElementText('lexy_opened_by') || getElementText('opened_by'),
                  documentName: getElementText('lexy_original_filename') || getElementText('original_filename') || getElementText('document_name') || getElementText('filename')
                };
                
                console.log('OfficeService: Found Lexy metadata:', metadata);
                return metadata;
              }
            }
          } catch (partError) {
            console.log(`OfficeService: Could not read custom XML part ${i}:`, partError.message);
          }
        }
        
        console.log('OfficeService: No Lexy metadata found in document');
        return null;
      });
    } catch (error) {
      console.error('OfficeService: Error reading Lexy metadata:', error);
      return null;
    }
  }

  /**
   * Check if document has Lexy connection (was opened from Lexy)
   * @returns {Promise<boolean>}
   */
  async hasLexyConnection() {
    const metadata = await this.getLexyMetadata();
    return metadata !== null && metadata.documentId !== null;
  }
  
  /**
   * Listen for selection changes in the document
   * @param {Function} callback - Called when selection changes
   */
  onSelectionChange(callback) {
    if (!this.isInOffice()) {
      return () => {}; // No-op unsubscribe
    }
    
    // Note: Word JavaScript API has limited event support
    // We'll poll for changes as a workaround
    let lastSelection = '';
    
    const checkSelection = async () => {
      try {
        const { text } = await this.getSelection();
        if (text !== lastSelection) {
          lastSelection = text;
          callback(text);
        }
      } catch (error) {
        console.error('OfficeService: Error checking selection:', error);
      }
    };
    
    const intervalId = setInterval(checkSelection, 500);
    
    return () => clearInterval(intervalId);
  }
  
  /**
   * Get the document as a file (for upload)
   * @returns {Promise<{blob: Blob, filename: string}>}
   */
  async getDocumentFile() {
    if (!this.isInOffice()) {
      console.log('OfficeService: Not in Office, cannot get document file');
      return null;
    }
    
    return new Promise((resolve, reject) => {
      Office.context.document.getFileAsync(
        Office.FileType.Compressed,
        { sliceSize: 4194304 }, // 4MB chunks
        async (result) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            reject(new Error(result.error.message));
            return;
          }
          
          const file = result.value;
          const sliceCount = file.sliceCount;
          const slices = [];
          
          try {
            // Read all slices
            for (let i = 0; i < sliceCount; i++) {
              const sliceData = await this._getSliceAsync(file, i);
              slices.push(sliceData);
            }
            
            // Combine slices into single array
            const docArray = this._concatenateArrays(slices);
            const blob = new Blob([docArray], {
              type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            });
            
            // Get filename from document properties
            const props = await this.getDocumentProperties();
            const filename = props.title ? `${props.title}.docx` : 'document.docx';
            
            file.closeAsync();
            resolve({ blob, filename });
          } catch (error) {
            file.closeAsync();
            reject(error);
          }
        }
      );
    });
  }
  
  /**
   * Helper to get a slice of the document
   */
  _getSliceAsync(file, sliceIndex) {
    return new Promise((resolve, reject) => {
      file.getSliceAsync(sliceIndex, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value.data);
        } else {
          reject(new Error(result.error.message));
        }
      });
    });
  }
  
  /**
   * Helper to concatenate typed arrays
   */
  _concatenateArrays(arrays) {
    let totalLength = 0;
    arrays.forEach(arr => totalLength += arr.length);
    
    const result = new Uint8Array(totalLength);
    let offset = 0;
    arrays.forEach(arr => {
      result.set(new Uint8Array(arr), offset);
      offset += arr.length;
    });
    
    return result;
  }

  /**
   * Get all comments from the document
   * @returns {Promise<Array>}
   */
  async getComments() {
    if (!this.isInOffice()) {
      console.log('OfficeService: Not in Office, returning empty comments');
      return [];
    }
    
    try {
      return await Word.run(async (context) => {
        // Get comments from document body
        const body = context.document.body;
        const comments = body.getComments();
        comments.load(['items']);
        await context.sync();
        
        // Load details for each comment
        const commentList = [];
        for (const comment of comments.items) {
          comment.load(['content', 'authorName', 'creationDate', 'id']);
          // Try to get the associated text
          try {
            const range = comment.getRange();
            range.load('text');
          } catch (e) {
            // Range might not be available
          }
        }
        await context.sync();
        
        for (const comment of comments.items) {
          let quotedText = '';
          try {
            const range = comment.getRange();
            range.load('text');
            await context.sync();
            quotedText = range.text || '';
          } catch (e) {
            // Ignore if range not available
          }
          
          commentList.push({
            id: comment.id,
            text: comment.content || '',
            author: comment.authorName || '',
            date: comment.creationDate?.toISOString() || new Date().toISOString(),
            quotedText: quotedText
          });
        }
        
        return commentList;
      });
    } catch (error) {
      console.error('OfficeService: Error getting comments:', error);
      // Return empty array on error (API might not be available)
      return [];
    }
  }

  /**
   * Get tracked changes from the document
   * Note: Office.js has limited support for tracked changes - requires WordApi 1.6+
   * @returns {Promise<Array>}
   */
  async getTrackedChanges() {
    if (!this.isInOffice()) {
      console.log('OfficeService: Not in Office, returning empty tracked changes');
      return [];
    }
    
    try {
      return await Word.run(async (context) => {
        const changeList = [];
        
        // Method 1: Try TrackedChanges API (Word API 1.6+)
        try {
          const body = context.document.body;
          
          // Check if the API is available
          if (typeof body.getTrackedChanges === 'function') {
            const trackedChanges = body.getTrackedChanges();
            trackedChanges.load(['items']);
            await context.sync();
            
            if (trackedChanges.items && trackedChanges.items.length > 0) {
              for (const change of trackedChanges.items) {
                change.load(['type', 'author', 'date', 'text']);
              }
              await context.sync();
              
              for (const change of trackedChanges.items) {
                changeList.push({
                  type: change.type || 'unknown',
                  author: change.author || '',
                  date: change.date?.toISOString() || new Date().toISOString(),
                  text: change.text || ''
                });
              }
              console.log(`OfficeService: Found ${changeList.length} tracked changes via API`);
              return changeList;
            }
          }
        } catch (apiError) {
          console.log('OfficeService: TrackedChanges API not available:', apiError.message);
        }
        
        // Method 2: Check if Track Changes is enabled and estimate from revision marks
        try {
          // We can at least detect if track changes mode is on
          const doc = context.document;
          doc.load('changeTrackingMode');
          await context.sync();
          
          if (doc.changeTrackingMode === 'TrackAll' || doc.changeTrackingMode === 'TrackMineOnly') {
            console.log('OfficeService: Track changes is enabled, but API not available to read changes');
            // Return indicator that track changes is on but we can't read the details
            return [{
              type: 'info',
              author: 'System',
              date: new Date().toISOString(),
              text: 'Sporede endringer er aktivert i dokumentet'
            }];
          }
        } catch (e) {
          console.log('OfficeService: Could not check change tracking mode');
        }
        
        return changeList;
      });
    } catch (error) {
      console.error('OfficeService: Error getting tracked changes:', error);
      return [];
    }
  }

  /**
   * Find and select text in the document
   * @param {string} textToFind - The text to find and select
   * @returns {Promise<boolean>}
   */
  async findAndSelect(textToFind: string): Promise<boolean> {
    if (!this.isInOffice() || !textToFind) {
      console.log('OfficeService: Not in Office or no text provided');
      return false;
    }
    
    try {
      return await Word.run(async (context: any) => {
        const body = context.document.body;
        
        // Search for the text
        const searchResults = body.search(textToFind.substring(0, 255), {
          matchCase: false,
          matchWholeWord: false
        });
        
        searchResults.load('items');
        await context.sync();
        
        if (searchResults.items.length > 0) {
          // Select the first match
          searchResults.items[0].select();
          await context.sync();
          console.log('OfficeService: Text found and selected');
          return true;
        }
        
        console.log('OfficeService: Text not found in document');
        return false;
      });
    } catch (error) {
      console.error('OfficeService: Error finding text:', error);
      return false;
    }
  }

  /**
   * Find text and replace it with new text
   * This is the key function for applying suggestions that replace existing content
   * @param {string} originalText - The text to find
   * @param {string} newText - The replacement text
   * @returns {Promise<boolean>}
   */
  async findAndReplace(originalText: string, newText: string): Promise<boolean> {
    if (!this.isInOffice() || !originalText || !newText) {
      console.log('OfficeService: Not in Office or missing text');
      return false;
    }
    
    try {
      return await Word.run(async (context: any) => {
        const body = context.document.body;
        
        // Search for the original text (truncate to 255 chars which is Word's search limit)
        const searchText = originalText.length > 200 
          ? originalText.substring(0, 200) 
          : originalText;
        
        const searchResults = body.search(searchText, {
          matchCase: false,
          matchWholeWord: false
        });
        
        searchResults.load('items');
        await context.sync();
        
        if (searchResults.items.length > 0) {
          // Replace the first match
          const range = searchResults.items[0];
          range.insertText(newText, Word.InsertLocation.replace);
          await context.sync();
          
          console.log('OfficeService: Text found and replaced');
          return true;
        }
        
        console.log('OfficeService: Original text not found, cannot replace');
        return false;
      });
    } catch (error) {
      console.error('OfficeService: Error finding and replacing text:', error);
      return false;
    }
  }

  /**
   * Find text, replace it, and add a comment explaining the change
   * Used for tracked changes simulation with rationale
   * @param {string} originalText - The text to find
   * @param {string} newText - The replacement text  
   * @param {string} rationale - Explanation for the change (optional)
   * @returns {Promise<boolean>}
   */
  async findAndReplaceWithComment(originalText: string, newText: string, rationale?: string): Promise<boolean> {
    if (!this.isInOffice() || !originalText || !newText) {
      console.log('OfficeService: Not in Office or missing text');
      return false;
    }
    
    try {
      return await Word.run(async (context: any) => {
        const body = context.document.body;
        
        // Search for the original text
        const searchText = originalText.length > 200 
          ? originalText.substring(0, 200) 
          : originalText;
        
        const searchResults = body.search(searchText, {
          matchCase: false,
          matchWholeWord: false
        });
        
        searchResults.load('items');
        await context.sync();
        
        if (searchResults.items.length > 0) {
          // Replace the first match
          const range = searchResults.items[0];
          range.insertText(newText, Word.InsertLocation.replace);
          await context.sync();
          
          // Select the newly inserted text to add comment
          const newRange = context.document.getSelection();
          
          // Add comment if rationale provided
          if (rationale) {
            try {
              const commentText = `Lexy AI endring: ${rationale.substring(0, 200)}`;
              newRange.insertComment(commentText);
              await context.sync();
            } catch (commentError) {
              console.log('OfficeService: Could not add comment:', commentError);
            }
          }
          
          console.log('OfficeService: Text replaced with comment');
          return true;
        }
        
        console.log('OfficeService: Original text not found, cannot replace');
        return false;
      });
    } catch (error) {
      console.error('OfficeService: Error in findAndReplaceWithComment:', error);
      return false;
    }
  }

  /**
   * Get complete work session data (document + comments + changes + metadata)
   * @returns {Promise<object>}
   */
  async getWorkSessionData() {
    console.log('OfficeService: Collecting work session data...');
    
    const [documentFile, comments, trackedChanges, properties, documentText] = await Promise.all([
      this.getDocumentFile().catch(e => {
        console.error('Error getting document file:', e);
        return null;
      }),
      this.getComments().catch(e => {
        console.error('Error getting comments:', e);
        return [];
      }),
      this.getTrackedChanges().catch(e => {
        console.error('Error getting tracked changes:', e);
        return [];
      }),
      this.getDocumentProperties().catch(e => {
        console.error('Error getting properties:', e);
        return { title: 'Unknown' };
      }),
      this.getDocumentText().catch(e => {
        console.error('Error getting document text:', e);
        return '';
      })
    ]);
    
    return {
      document: documentFile,
      documentText: documentText,
      properties: properties,
      comments: comments,
      trackedChanges: trackedChanges,
      collectedAt: new Date().toISOString()
    };
  }
}

// Singleton instance
export const officeService = new OfficeService();
export default officeService;
